import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { SiteTasksSubspaceName } from '../../state/SpaceInfo';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { TasksSpaceProvider, TasksSpaceProviderProps } from '../TasksSpace/TasksSpaceProvider';

export interface SiteTasksSpaceRouteParams {}

export interface SiteTasksRouteComponentProps extends RouteComponentProps<SiteTasksSpaceRouteParams> {}

export interface SiteTasksSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly provider: (subspaceName: SiteTasksSubspaceName, providerProps: SiteTasksSpaceComponentWithRouterProps) => (props: SiteTasksRouteComponentProps) => JSX.Element;
}

export type SiteTasksSpaceRouterProps = SiteTasksSpaceRouterOwnProps;

export class SiteTasksSpaceRouterComponent extends React.Component<SiteTasksSpaceRouterProps> {
  private server: SessionManagementService;

  public constructor(props: SiteTasksSpaceRouterProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.tasksExtractRefreshesPath()} render={this.props.provider('extractRefreshes', this.props)}/>
        {this.protectedRouteForFlowsSubspace(this.server)}
        <Route exact path={this.props.urls.tasksSubscriptionsPath()} render={this.props.provider('subscriptions', this.props)}/>
        {this.protectedRouteForDataAlertsSubspace(this.server)}
        {this.protectedRouteForDataAccelerationSubspace(this.server)}
        {this.protectedRouteForLinkedTasksSubspace(this.server)}

        <Route exact path={this.props.urls.tasksPath()} render={() => (
          <Redirect to={this.props.urls.tasksExtractRefreshesPath()} />
        )} />
        <Route render={this.error}/>
      </Switch>
    );
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }

  private protectedRouteForDataAccelerationSubspace(server: SessionManagementService) {
    return FeatureFlags.workbookAccelerationUIEnabled &&
      <Route
          exact path={this.props.urls.tasksDataAccelerationPath()}
          render={this.props.provider('dataAcceleration', this.props)}
      />;
  }

  private protectedRouteForFlowsSubspace(server: SessionManagementService) {
    return server.flowsOnSiteEnabled() &&
      <Route
        exact path={this.props.urls.tasksFlowsPath()}
        render={this.props.provider('runFlowTasks', this.props)}
      />;
  }

  private protectedRouteForLinkedTasksSubspace(server: SessionManagementService) {
    return FeatureFlags.LinkedTaskSchedulingEnabled &&
      <Route
        exact path={this.props.urls.tasksLinkedTasksPath()}
        render={this.props.provider('linkedTasks', this.props)}
      />;
  }

  private protectedRouteForDataAlertsSubspace(server: SessionManagementService) {
    if (FeatureFlags.alertOnThresholdConditionEnabled && !server.isDataAlertsEnabled()) {
      return (
        <Route
          exact path={this.props.urls.tasksDataAlertsPath()}
          render={() => <RouterError errorType={'data-alerts-disabled'} urls={this.props.urls}/>}
        />
      );
    }

    return FeatureFlags.alertOnThresholdConditionEnabled && server.isDataAlertsEnabled() &&
      <Route
        exact path={this.props.urls.tasksDataAlertsPath()}
        render={this.props.provider('alerts', this.props)}
      />;
  }
}

export type SiteTasksSpaceComponentWithRouterProps = Omit<SiteTasksSpaceRouterProps, 'provider'>;
const provider = (subspaceName: SiteTasksSubspaceName, providerProps: SiteTasksSpaceComponentWithRouterProps): (props: SiteTasksRouteComponentProps) => JSX.Element => {
  return (props: SiteTasksRouteComponentProps): JSX.Element => {
    const spaceProps: TasksSpaceProviderProps = {
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      siteName: providerProps.siteName,
      subspaceName,
      urls: providerProps.urls,
      inServerArea: false,
    };
    return <TasksSpaceProvider {...spaceProps} />;
  };
};

export type TasksSpaceComponentOwnProps = Omit<SiteTasksSpaceRouterOwnProps, 'provider'>;
const SiteTasksSpaceComponent = (props: SiteTasksSpaceComponentWithRouterProps) => {
  return (
    <SiteTasksSpaceRouterComponent {...props} provider={provider}/>
  );
};

export const SiteTasksSpaceRouter: React.FunctionComponent<TasksSpaceComponentOwnProps> = SiteTasksSpaceComponent;
